/**
 * Tutorial Data — web port of client-native/src/features/tutorial/lib/tutorialData.ts.
 *
 * Static content for the 4 tutorial phases (12 items total). Copy strings,
 * emoji, ids, actionKeys and extraInfo are reproduced VERBATIM from iOS — incl.
 * the iOS copy typos in `share_with_friend` ("a accountability", "langauge")
 * which are kept for parity.
 *
 * iOS loads PNG icons from assets/tutorial/*; the same PNGs are copied into
 * ./assets and wired via `iconUrl` so the checklist renders the real icons (the
 * emoji is only the fallback when the image fails to load, matching iOS).
 */

import { TutorialPhase } from "./types";
import placementTestIcon from "../assets/placement-test.png";
import clickWordIcon from "../assets/click-word.png";
import highlightPhraseIcon from "../assets/highlight-phrase.png";
import saveWordIcon from "../assets/save-word.png";
import completeActivityIcon from "../assets/complete-activity.png";
import skipActivityIcon from "../assets/skip-activity.png";
import completeLessonIcon from "../assets/complete-lesson.png";
import openBookIcon from "../assets/open-book.png";
import reviewCardsIcon from "../assets/review-cards.png";
import parrotMethodIcon from "../assets/parrot-method.png";
import customizeIcon from "../assets/customize.png";
import shareIcon from "../assets/share.png";

export const TUTORIAL_PHASES: TutorialPhase[] = [
  {
    id: "phase_1",
    phaseNumber: 1,
    items: [
      {
        id: "placement_test",
        emoji: "🧠",
        iconUrl: placementTestIcon,
        title: "Find your Spanish level",
        actionKey: "tutorial.placement_test.complete",
      },
      {
        id: "click_word_demo",
        emoji: "👆",
        iconUrl: clickWordIcon,
        title: "Click a word",
        actionKey: "tutorial.click_word.complete",
      },
      {
        id: "highlight_phrase_demo",
        emoji: "✍️",
        iconUrl: highlightPhraseIcon,
        title: "Highlight a phrase",
        actionKey: "tutorial.highlight_phrase.complete",
      },
      {
        id: "save_to_known_demo",
        emoji: "🔖",
        iconUrl: saveWordIcon,
        title: "Save a word to known",
        actionKey: "tutorial.save_word.complete",
      },
    ],
  },
  {
    id: "phase_2",
    phaseNumber: 2,
    items: [
      {
        id: "complete_activity",
        emoji: "✨",
        iconUrl: completeActivityIcon,
        title: "Complete an activity",
        actionKey: "tutorial.complete_activity.complete",
        extraInfo:
          "Activities are personalized based on YOUR learning words to help you practice and reinforce your learning.",
      },
      {
        id: "skip_activity_demo",
        emoji: "⏭️",
        iconUrl: skipActivityIcon,
        title: "Skip an activity",
        actionKey: "tutorial.skip_activity.complete",
      },
      {
        id: "complete_lesson",
        emoji: "🎬",
        iconUrl: completeLessonIcon,
        title: "Complete a lesson",
        actionKey: "tutorial.complete_lesson.complete",
        extraInfo:
          "Watch a video and complete the activities every day to progress through the lessons.",
      },
    ],
  },
  {
    id: "phase_3",
    phaseNumber: 3,
    items: [
      {
        id: "review_dictionary",
        emoji: "📖",
        iconUrl: openBookIcon,
        title: "Review your dictionary",
        actionKey: "tutorial.review_dictionary.complete",
        extraInfo:
          "Look at words and phrases you've saved to learning and known. You can move things between these two lists whenever you want.",
      },
      {
        id: "review_cards",
        emoji: "🔄",
        iconUrl: reviewCardsIcon,
        title: "Review your flashcards",
        actionKey: "tutorial.review_cards.complete",
        extraInfo:
          "Complete review of words you've previously seen through spaced repetition.",
      },
    ],
  },
  {
    id: "phase_4",
    phaseNumber: 4,
    items: [
      {
        id: "parrot_method",
        emoji: "🦜",
        iconUrl: parrotMethodIcon,
        title: "Learn the Parrot Method",
        actionKey: "tutorial.parrot_method.complete",
      },
      {
        id: "customize_experience",
        emoji: "⚙️",
        iconUrl: customizeIcon,
        title: "Visit your settings",
        actionKey: "tutorial.customize_experience.complete",
      },
      {
        id: "share_with_friend",
        emoji: "🤝",
        iconUrl: shareIcon,
        title: "Add accountability partner",
        actionKey: "tutorial.share_with_friend.complete",
        // iOS copy verbatim (incl. "a accountability" / "langauge" typos).
        extraInfo:
          "Studies show that having a accountability partner increases your chances of langauge acquisition by 62%.",
      },
    ],
  },
];

/** Total number of tutorial items across all phases (12). */
export const getTotalItemCount = (): number =>
  TUTORIAL_PHASES.reduce((total, phase) => total + phase.items.length, 0);

/** All item IDs in order. */
export const getAllItemIds = (): string[] =>
  TUTORIAL_PHASES.flatMap((phase) => phase.items.map((item) => item.id));
